import { useState, useEffect } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FaMobileAlt, FaBitcoin, FaClock, FaBolt, FaTag, FaUniversity } from 'react-icons/fa';
import API from '../api/axios';
import { useAuth } from '../context/AuthContext';
import Navbar from '../components/Navbar';

const Checkout = () => {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [service, setService] = useState(null);
  const [siteSettings, setSiteSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [rushDelivery, setRushDelivery] = useState(false);
  const [couponCode, setCouponCode] = useState('');
  const [discount, setDiscount] = useState(0);
  const [couponApplied, setCouponApplied] = useState(false);
  const packageIndex = location.state?.packageIndex || 0;

  const [form, setForm] = useState({ requirements: '', additionalNotes: '', paymentMethod: '', transactionId: '', senderNumber: '', cryptoTxHash: '' });

  useEffect(() => {
    if (!user) { navigate('/login'); return; }
    Promise.all([
      API.get(`/services/${id}`),
      API.get('/settings/public')
    ]).then(([serviceRes, settingsRes]) => {
      setService(serviceRes.data.service);
      setSiteSettings(settingsRes.data);
      // Set first enabled payment method as default
      const methods = settingsRes.data.paymentMethods;
      if (methods) { const firstKey = Object.keys(methods)[0]; if (firstKey) setForm(f => ({ ...f, paymentMethod: firstKey })); }
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [id, user, navigate]);

  if (loading) return (<><Navbar /><div style={{ paddingTop: '120px', textAlign: 'center', color: 'var(--text-muted)' }}>Loading...</div></>);
  if (!service) return (<><Navbar /><div style={{ paddingTop: '120px', textAlign: 'center', color: 'var(--text-muted)' }}>Service not found</div></>);

  const currency = siteSettings?.currency?.symbol || '$';
  const paymentMethods = siteSettings?.paymentMethods || {};
  const pkg = service.packages?.length > 0 ? service.packages[packageIndex] : null;
  const basePrice = pkg ? pkg.price : (service.discountPrice || service.price);
  const deliveryDays = pkg ? pkg.deliveryDays : service.deliveryTime;
  const rushExtra = rushDelivery && service.rushDelivery?.available ? service.rushDelivery.extraPrice : 0;
  const totalAmount = Math.max(basePrice + rushExtra - discount, 0);
  const finalDeliveryDays = rushDelivery && service.rushDelivery?.available ? service.rushDelivery.deliveryDays : deliveryDays;

  const paymentIcons = { bkash: '🅱️', nagad: '🟠', rocket: '🚀', crypto: '₿', bank: '🏦' };
  const paymentColors = { bkash: '#E2136E', nagad: '#F26522', rocket: '#8C3494', crypto: '#F7931A', bank: '#1a73e8' };
  const currentPayment = paymentMethods[form.paymentMethod];
  const isCrypto = form.paymentMethod === 'crypto';
  const isBank = form.paymentMethod === 'bank';

  const applyCoupon = async () => {
    if (!couponCode) return;
    try {
      const res = await API.post('/coupons/validate', { code: couponCode, amount: basePrice + rushExtra });
      setDiscount(res.data.discount); setCouponApplied(true);
      toast.success(`Coupon applied! -${currency}${res.data.discount}`);
    } catch (err) { toast.error(err.response?.data?.message || 'Invalid coupon'); setDiscount(0); setCouponApplied(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isCrypto && !isBank && !form.transactionId) return toast.error('Please enter transaction ID');
    if (isCrypto && !form.cryptoTxHash) return toast.error('Please enter transaction hash');
    setSubmitting(true);
    try {
      const res = await API.post('/orders', {
        serviceId: id, packageType: pkg?.name || 'Basic', requirements: form.requirements, additionalNotes: form.additionalNotes,
        paymentMethod: form.paymentMethod, isRushDelivery: rushDelivery, couponCode: couponApplied ? couponCode : '',
        paymentDetails: { transactionId: form.transactionId, senderNumber: form.senderNumber, cryptoTxHash: form.cryptoTxHash }
      });
      toast.success(`Order placed! Order #${res.data.orderNumber}`);
      navigate('/dashboard/orders');
    } catch (err) { toast.error(err.response?.data?.message || 'Failed to place order'); }
    setSubmitting(false);
  };

  return (
    <>
      <Navbar />
      <div style={{ paddingTop: '100px', minHeight: '100vh', paddingBottom: '60px' }}>
        <div className="container">
          <h1 style={{ fontSize: '2rem', marginBottom: '30px', textAlign: 'center' }}>Checkout</h1>
          <div style={{ display: 'flex', gap: '30px', flexWrap: 'wrap' }}>
            <div style={{ flex: '2', minWidth: '300px' }}>
              <form onSubmit={handleSubmit}>
                <div className="admin-card">
                  <h3 style={{ marginBottom: '15px', color: 'var(--primary)' }}>Project Requirements</h3>
                  <div className="form-group"><label>Describe what you need *</label>
                    <textarea value={form.requirements} onChange={e => setForm({ ...form, requirements: e.target.value })} required placeholder="Describe your project in detail..." style={{ minHeight: '150px' }} /></div>
                  <div className="form-group"><label>Additional Notes</label>
                    <textarea value={form.additionalNotes} onChange={e => setForm({ ...form, additionalNotes: e.target.value })} placeholder="Links, references, etc." /></div>
                </div>

                {service.rushDelivery?.available && (
                  <div className="admin-card" style={{ cursor: 'pointer' }} onClick={() => setRushDelivery(!rushDelivery)}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer' }}>
                      <input type="checkbox" checked={rushDelivery} onChange={e => setRushDelivery(e.target.checked)} style={{ width: '20px', height: '20px', accentColor: 'var(--primary)' }} />
                      <FaBolt style={{ color: 'var(--secondary)' }} />
                      <div><strong>Rush Delivery</strong> - {service.rushDelivery.deliveryDays} day <span style={{ color: 'var(--secondary)' }}>+{currency}{service.rushDelivery.extraPrice}</span></div>
                    </label>
                  </div>
                )}

                {/* Dynamic Payment Methods from Admin Settings */}
                <div className="admin-card">
                  <h3 style={{ marginBottom: '15px', color: 'var(--primary)' }}>Payment Method</h3>
                  <div style={{ display: 'flex', gap: '10px', marginBottom: '20px', flexWrap: 'wrap' }}>
                    {Object.entries(paymentMethods).map(([key, info]) => (
                      <button key={key} type="button" onClick={() => setForm({ ...form, paymentMethod: key })}
                        style={{ flex: 1, minWidth: '100px', padding: '15px 10px', borderRadius: '12px', cursor: 'pointer', fontFamily: 'Poppins', textAlign: 'center',
                          border: form.paymentMethod === key ? `2px solid ${paymentColors[key] || '#6c63ff'}` : '2px solid var(--border)',
                          background: form.paymentMethod === key ? `${paymentColors[key] || '#6c63ff'}20` : 'var(--bg-dark)', color: 'var(--text-primary)' }}>
                        <div style={{ fontSize: '1.5rem', marginBottom: '5px' }}>{paymentIcons[key] || '💳'}</div>
                        <div style={{ fontWeight: '600', fontSize: '0.85rem' }}>{info.name}</div>
                      </button>
                    ))}
                  </div>

                  {currentPayment && (
                    <div style={{ padding: '15px', background: 'var(--bg-dark)', borderRadius: '12px', marginBottom: '15px' }}>
                      {isCrypto ? (
                        <div>
                          <p style={{ color: 'var(--text-secondary)', marginBottom: '8px' }}>Send <strong style={{ color: 'var(--primary)' }}>{totalAmount} {currentPayment.coin || 'USDT'}</strong> to:</p>
                          <code style={{ color: '#F7931A', fontSize: '0.8rem', wordBreak: 'break-all' }}>{currentPayment.network}: {currentPayment.walletAddress}</code>
                        </div>
                      ) : isBank ? (
                        <div>
                          <p style={{ color: 'var(--text-secondary)', marginBottom: '8px' }}>Transfer <strong style={{ color: 'var(--primary)' }}>{currency}{totalAmount}</strong> to:</p>
                          <p><FaUniversity /> <strong>{currentPayment.bankName}</strong></p>
                          <p>A/C Name: {currentPayment.accountName}</p>
                          <p>A/C No: {currentPayment.accountNumber}</p>
                          {currentPayment.branch && <p>Branch: {currentPayment.branch}</p>}
                        </div>
                      ) : (
                        <div>
                          <p style={{ color: 'var(--text-secondary)', marginBottom: '8px' }}>Send <strong style={{ color: 'var(--primary)' }}>{currency}{totalAmount}</strong> to:</p>
                          <p style={{ color: paymentColors[form.paymentMethod], fontSize: '1.2rem', fontWeight: '700' }}><FaMobileAlt /> {currentPayment.number}</p>
                          <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem', marginTop: '5px' }}>{currentPayment.type || 'Send Money'}</p>
                        </div>
                      )}
                      {currentPayment.instructions && <p style={{ color: 'var(--text-muted)', fontSize: '0.8rem', marginTop: '10px', fontStyle: 'italic' }}>{currentPayment.instructions}</p>}
                    </div>
                  )}

                  {isCrypto ? (
                    <div className="form-group"><label><FaBitcoin /> Transaction Hash *</label>
                      <input type="text" value={form.cryptoTxHash} onChange={e => setForm({ ...form, cryptoTxHash: e.target.value })} required placeholder="0x..." /></div>
                  ) : (
                    <>
                      <div className="form-group"><label>Transaction ID *</label>
                        <input type="text" value={form.transactionId} onChange={e => setForm({ ...form, transactionId: e.target.value })} required placeholder="e.g. 8N7XXXXXX" /></div>
                      <div className="form-group"><label>Sender Number</label>
                        <input type="text" value={form.senderNumber} onChange={e => setForm({ ...form, senderNumber: e.target.value })} placeholder="01XXXXXXXXX" /></div>
                    </>
                  )}
                </div>

                <button type="submit" className="btn btn-primary" disabled={submitting}
                  style={{ width: '100%', justifyContent: 'center', padding: '15px', fontSize: '1.1rem' }}>
                  {submitting ? 'Placing Order...' : `Place Order - ${currency}${totalAmount}`}
                </button>
              </form>
            </div>

            <div style={{ flex: '1', minWidth: '280px' }}>
              <div style={{ background: 'var(--bg-card)', borderRadius: '20px', padding: '25px', border: '1px solid var(--border)', position: 'sticky', top: '100px' }}>
                <h3 style={{ marginBottom: '20px', color: 'var(--primary)' }}>Order Summary</h3>
                <h4 style={{ marginBottom: '10px' }}>{service.title}</h4>
                {pkg && <p style={{ color: 'var(--text-muted)', marginBottom: '15px' }}>Package: {pkg.name}</p>}
                <div style={{ borderTop: '1px solid var(--border)', paddingTop: '15px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px', color: 'var(--text-secondary)' }}><span>Base</span><span>{currency}{basePrice}</span></div>
                  {rushExtra > 0 && <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px', color: 'var(--secondary)' }}><span><FaBolt /> Rush</span><span>+{currency}{rushExtra}</span></div>}
                  {discount > 0 && <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px', color: '#00d2ff' }}><span><FaTag /> Discount</span><span>-{currency}{discount}</span></div>}
                  <div style={{ borderTop: '1px solid var(--border)', paddingTop: '10px', display: 'flex', justifyContent: 'space-between', fontWeight: '700', fontSize: '1.2rem' }}>
                    <span>Total</span><span style={{ color: 'var(--primary)' }}>{currency}{totalAmount}</span>
                  </div>
                </div>
                <div style={{ marginTop: '15px', padding: '10px', background: 'var(--bg-dark)', borderRadius: '10px', display: 'flex', alignItems: 'center', gap: '8px', color: 'var(--text-muted)', fontSize: '0.85rem' }}>
                  <FaClock /> Delivery in {finalDeliveryDays} days
                </div>
                <div style={{ marginTop: '15px', display: 'flex', gap: '8px' }}>
                  <input type="text" value={couponCode} onChange={e => setCouponCode(e.target.value)} placeholder="Coupon code"
                    style={{ flex: 1, padding: '10px', background: 'var(--bg-dark)', border: '1px solid var(--border)', borderRadius: '10px', color: 'var(--text-primary)', fontFamily: 'Poppins' }} />
                  <button type="button" onClick={applyCoupon} className="btn btn-outline btn-small" disabled={couponApplied}>{couponApplied ? '✓' : 'Apply'}</button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Checkout;
